import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";
import { cn } from "@/lib/utils";
import { ImageIcon } from "lucide-react";

export function ScenarioThumbnailField(props: {
  id: string;
  /** Shown when no new file is chosen (e.g. current URL from API). */
  existingUrl?: string | null;
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}) {
  const { id, existingUrl, value, onChange, disabled } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [objectPreview, setObjectPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setObjectPreview(null);
      return;
    }
    const u = URL.createObjectURL(value);
    setObjectPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [value]);

  const displaySrc =
    objectPreview || (existingUrl && !value ? resolveMediaUrl(existingUrl) ?? existingUrl : null);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Thumbnail (optional)</Label>
      <Input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        disabled={disabled}
        className="cursor-pointer"
        onChange={(e) => {
          const f = e.target.files?.[0];
          onChange(f ?? null);
        }}
      />
      <p className="text-xs text-muted-foreground">
        PNG, JPG, JPEG, WebP, or GIF. Choose a file to upload or replace the current image.
      </p>
      {displaySrc && (
        <div
          className={cn(
            "relative overflow-hidden rounded-md border border-border bg-muted",
            "max-h-48 w-full max-w-xs aspect-video",
          )}
        >
          <img src={displaySrc} alt="Thumbnail preview" className="h-full w-full object-cover" />
        </div>
      )}
      {!value && existingUrl && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <ImageIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Current image is shown. Pick a new file to replace it on save.
        </p>
      )}
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => {
            onChange(null);
            if (inputRef.current) inputRef.current.value = "";
          }}
        >
          Remove selected file
        </Button>
      )}
    </div>
  );
}
